import React from "react";
import { RouteStatus } from "@racoon/api";
import { cn } from "@racoon/ui";
import { STATUS_TONE_CLASS, type StatusTone } from "@racoon/lib";
import { Namespace, useT, type TFn } from "@racoon/i18n";

type RouteStatusBadgeProps = {
  status: RouteStatus;
  jobs?: any[];
  completedJobs?: number;
  onStatusClick?: () => void;
};

const toneClass = (tone: StatusTone) =>
  `${STATUS_TONE_CLASS[tone].bg} ${STATUS_TONE_CLASS[tone].color}`;

export const getStatusDisplay = (
  t: TFn,
  status: RouteStatus,
  jobs?: any[],
  completedJobs?: number,
) => {
  const totalJobs = jobs?.length || 0;
  const completed = completedJobs || 0;
  // Show progress for active statuses
  const showProgress = totalJobs > 1;

  const withProgress = (text: string) =>
    showProgress
      ? t(Namespace.Routes, "statusWithProgress", {
          status: text,
          completed,
          total: totalJobs,
        })
      : text;

  switch (status) {
    case RouteStatus.ROUTE_STATUS_CREATED:
      return {
        text: withProgress(t(Namespace.Routes, "statusAssigned")),
        className: toneClass("assigned"),
      };
    case RouteStatus.ROUTE_STATUS_IN_PROGRESS:
      return {
        text: withProgress(t(Namespace.Routes, "statusInProgress")),
        className: toneClass("active"),
      };
    case RouteStatus.ROUTE_STATUS_RETURNING:
      return {
        text: t(Namespace.Routes, "statusReturning"),
        className: toneClass("active"),
      };
    case RouteStatus.ROUTE_STATUS_FORCE_COMPLETED:
    case RouteStatus.ROUTE_STATUS_COMPLETED:
      return {
        text: t(Namespace.Routes, "statusCompleted"),
        className: toneClass("done"),
      };
    case RouteStatus.ROUTE_STATUS_CANCELLED:
      return {
        text: t(Namespace.Routes, "statusCancelled"),
        className: toneClass("cancelled"),
      };
    case RouteStatus.ROUTE_STATUS_NOT_APPROVED:
      return {
        text: t(Namespace.Routes, "statusNotApproved"),
        className: toneClass("pending"),
      };
    case RouteStatus.ROUTE_STATUS_UNDEFINED:
    default:
      return {
        text: t(Namespace.Routes, "statusUndefined"),
        className: "bg-gray-100 text-gray-500",
      };
  }
};

export const RouteStatusBadge: React.FC<RouteStatusBadgeProps> = ({
  status,
  jobs,
  completedJobs,
  onStatusClick,
}) => {
  const t = useT();
  const { text, className } = getStatusDisplay(t, status, jobs, completedJobs);
  const isClickable = onStatusClick && jobs && jobs.length > 1;

  return (
    <span
      className={cn(
        "flex justify-center items-center py-1 rounded-full text-xs font-medium whitespace-nowrap",
        className,
        "!min-w-[120px] !max-w-[120px] !w-[120px]",
        isClickable && "cursor-pointer hover:opacity-80 transition-opacity",
      )}
      onClick={isClickable ? onStatusClick : undefined}
      title={isClickable ? t(Namespace.Routes, "statusBadgeTooltip") : undefined}
    >
      {text}
    </span>
  );
};
